/**
 * Asset fields a spreadsheet column can feed, in the order the import dialog
 * lists them.
 */
export const ASSET_IMPORT_FIELDS = [
  { field: 'name', label: 'Name', required: true },
  { field: 'site_id', label: 'Site ID' },
  { field: 'address', label: 'Address' },
  { field: 'city', label: 'City' },
  { field: 'state', label: 'State' },
  { field: 'postal_code', label: 'Postal code' },
  { field: 'country', label: 'Country' },
  { field: 'latitude', label: 'Latitude' },
  { field: 'longitude', label: 'Longitude' },
  { field: 'type', label: 'Type' },
  { field: 'description', label: 'Description' },
] as const;

export type AssetImportField = (typeof ASSET_IMPORT_FIELDS)[number]['field'];
export type AssetColumnMapping = Partial<Record<AssetImportField, string>>;
export type GeocodeMode = 'auto' | 'coordinates_only';

/**
 * Map icons an import can set for every asset; the server accepts only these
 * (`IMPORT_SYMBOLOGY_KEYS`).
 */
export const ASSET_IMPORT_SYMBOLOGY_KEYS: readonly string[] = [
  'facility',
  'hospital',
  'fireStation',
  'policeStation',
  'emergencyOperationsCenter',
  'helibase',
  'incidentBase',
  'hazardOrigin',
];

export interface AssetImportDefaultsInput {
  kind?: string;
  symbology?: string;
  threats?: string[];
  automation_rule_ids?: string[];
}

export interface SavedAssetImportMapping {
  column_mapping: AssetColumnMapping;
  defaults: AssetImportDefaultsInput & { geocode_mode?: GeocodeMode };
  updated_at: string;
}

export interface ParsedImportSheet {
  name: string;
  headers: string[];
  rowCount: number;
  sample: Record<string, unknown>[];
  headerSignature: string;
  suggestedMapping: AssetColumnMapping;
  savedMapping: SavedAssetImportMapping | null;
}

export interface ParsedImportFile {
  fileName: string;
  maxRows: number;
  sheets: ParsedImportSheet[];
}

/** First sheet with rows, else the first sheet. */
export function defaultSheet(
  file: ParsedImportFile
): ParsedImportSheet | undefined {
  return file.sheets.find((s) => s.rowCount > 0) ?? file.sheets[0];
}

/**
 * A saved mapping for this file layout wins over the header-based suggestion;
 * columns it names must still exist.
 */
export function initialMapping(sheet: ParsedImportSheet): AssetColumnMapping {
  const source = sheet.savedMapping?.column_mapping ?? sheet.suggestedMapping;
  const mapping: AssetColumnMapping = {};
  for (const { field } of ASSET_IMPORT_FIELDS) {
    const header = source[field];
    if (header && sheet.headers.includes(header)) mapping[field] = header;
  }
  return mapping;
}

/** Why the mapping can't be imported yet, or null when it can. */
export function mappingProblem(mapping: AssetColumnMapping): string | null {
  if (!mapping.name) return 'Choose the column that holds each asset name.';
  const hasCoordinates = !!mapping.latitude && !!mapping.longitude;
  if (
    !mapping.address &&
    !hasCoordinates &&
    (mapping.latitude || mapping.longitude)
  ) {
    return 'Choose both Latitude and Longitude, or an Address column.';
  }
  if (!mapping.address && !hasCoordinates) {
    return 'Choose an Address column, or both Latitude and Longitude.';
  }
  const used = Object.values(mapping).filter(Boolean);
  if (new Set(used).size !== used.length)
    return 'Each column can feed only one field.';
  return null;
}

/**
 * Defaults to start the defaults step with: the saved ones for this layout,
 * else none.
 */
export function initialDefaults(sheet: ParsedImportSheet): {
  defaults: AssetImportDefaultsInput;
  geocodeMode: GeocodeMode;
} {
  const saved = sheet.savedMapping?.defaults;
  if (!saved) return { defaults: {}, geocodeMode: 'auto' };
  const { geocode_mode, ...defaults } = saved;
  return { defaults, geocodeMode: geocode_mode ?? 'auto' };
}
